import { UserIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import logo from '../../../assets/logos/aye-logo.png';
import PayNetFooter from '../../../shared/PayNetFooter';
import { notifyExternalRegistrationResult } from '../utils/external-session-notify';

// OngLaye frontend URL for redirect after registration
const ONGLAYE_URL =
  process.env.REACT_APP_ONGLAYE_URL || 'https://onglaye.dev.ayeface.com';

// Checkout frontend URL for redirect after registration
const CHECKOUT_URL =
  process.env.REACT_APP_CHECKOUT_URL || 'https://checkout.dev.ayeface.com';

export default function RegistrationScreenSuccess() {
  const [isOngLayeFlow, setIsOngLayeFlow] = useState(false);
  const [isCheckoutFlow, setIsCheckoutFlow] = useState(false);
  const [ong_key, setOngKey] = useState<string | null>(null);
  const [checkout_session_id, setCheckoutSessionId] = useState<string | null>(
    null
  );
  const [countdown, setCountdown] = useState(5);

  // Whether this is an external payment flow (OngLaye or Checkout)
  const isExternalFlow = isOngLayeFlow || isCheckoutFlow;

  useEffect(() => {
    // Check if this was an OngLaye registration flow
    const storedIsOngLayeFlow =
      sessionStorage.getItem('is_onglaye_flow') === 'true';
    const storedOngKey = sessionStorage.getItem('ong_key');

    // Check if this was a Checkout registration flow
    const storedIsCheckoutFlow =
      sessionStorage.getItem('is_checkout_flow') === 'true';
    const storedCheckoutSessionId = sessionStorage.getItem(
      'checkout_session_id'
    );

    notifyExternalRegistrationResult('success', {
      paymentStatus: 'success'
    });

    setIsOngLayeFlow(storedIsOngLayeFlow);
    setOngKey(storedOngKey);
    setIsCheckoutFlow(storedIsCheckoutFlow);
    setCheckoutSessionId(storedCheckoutSessionId);

    // Auto-redirect for OngLaye flow after countdown
    if (storedIsOngLayeFlow && storedOngKey) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            // Clear OngLaye session data
            sessionStorage.removeItem('is_onglaye_flow');
            sessionStorage.removeItem('ong_key');
            // Redirect to OngLaye payment page
            window.location.href = `${ONGLAYE_URL}/payment?ong_key=${storedOngKey}`;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }

    // Auto-redirect for Checkout flow after countdown
    if (storedIsCheckoutFlow && storedCheckoutSessionId) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            // Clear checkout session data
            sessionStorage.removeItem('is_checkout_flow');
            sessionStorage.removeItem('checkout_session_id');
            sessionStorage.removeItem('checkout_biz_token');
            sessionStorage.removeItem('checkout_face_index');
            // Redirect to checkout payment page to continue the flow
            window.location.href = `${CHECKOUT_URL}/payment?session_id=${storedCheckoutSessionId}`;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, []);

  const handleContinueToPayment = () => {
    if (isOngLayeFlow && ong_key) {
      // Clear OngLaye session data
      sessionStorage.removeItem('is_onglaye_flow');
      sessionStorage.removeItem('ong_key');
      // Redirect to OngLaye payment page
      window.location.href = `${ONGLAYE_URL}/payment?ong_key=${ong_key}`;
    } else if (isCheckoutFlow && checkout_session_id) {
      // Clear checkout session data
      sessionStorage.removeItem('is_checkout_flow');
      sessionStorage.removeItem('checkout_session_id');
      sessionStorage.removeItem('checkout_biz_token');
      sessionStorage.removeItem('checkout_face_index');
      // Redirect to checkout payment page to continue the flow
      window.location.href = `${CHECKOUT_URL}/payment?session_id=${checkout_session_id}`;
    }
  };

  return (
    <div className="flex h-[100vh] flex-col items-center justify-center">
      <div className="mt-4 flex w-full flex-shrink-0 flex-col items-center justify-center">
        <img src={logo} alt="Aye Logo" className="mt-9 h-8 w-16" />
      </div>
      <div className="mx-4 flex flex-1 flex-col items-center justify-start gap-8 text-center text-lg font-medium text-gray-600">
        <div className="relative mt-16 flex w-full flex-row items-center justify-center">
          <img
            src={require('../../../assets/success_bg.png')}
            alt="success_bg"
            className="object-contain"
          />
          <UserIcon className="absolute left-1/2 top-1/2 -ml-1 mt-4 h-9 w-9 -translate-x-1/2 -translate-y-1/2 text-white" />
        </div>
        <div className="mt-6 flex flex-col items-center justify-center gap-8">
          <p className="text-center text-2xl font-semibold text-black">
            Registration successful!
          </p>
          <p className="mt-2">
            {isExternalFlow
              ? 'Your registration is complete! Redirecting you back to continue your payment...'
              : 'You can now use your face for seamless payments and more to come'}
          </p>
        </div>
        <div className="flex flex-col items-center justify-center gap-6">
          {isExternalFlow ? (
            <>
              <button
                onClick={handleContinueToPayment}
                className="w-48 rounded-2xl bg-[#5CB891] px-6 py-4 text-center text-lg font-semibold text-white transition-all duration-300 ease-out hover:bg-[#4aa07c]"
              >
                Continue to Payment
              </button>
              <p className="text-center text-sm text-gray-500">
                Redirecting in {countdown} seconds...
              </p>
            </>
          ) : (
            <>
              <a
                href={window.location.origin}
                className="w-48 rounded-2xl bg-[#5CB891] px-6 py-4 text-center text-lg font-semibold text-white transition-all duration-300 ease-out hover:bg-[#4aa07c]"
              >
                Login
              </a>
              <p className="text-center text-sm text-gray-500">
                To manage your payments and more
              </p>
            </>
          )}
        </div>
      </div>
      <div className="my-4">
        <PayNetFooter />
      </div>
    </div>
  );
}
